import React, { useState } from "react"
import { Box, Flex, Image, Text, Textarea, Button, IconButton, Icon, Tooltip } from "@chakra-ui/react"
import { HiOutlineTrash } from "react-icons/hi2"
import questionBubbleIconUnanswered from "../Images/question_bubble-icon-unanswered.svg"
import questionBubbleIconAnswered from "../Images/question_bubble_icon_answered.svg"
import { c } from "../classroomColors"

/**
 * One pre-class question. Collapsed it matches the Figma bubble (truncated
 * text + status dot). Clicking expands it: the full question, the teacher's
 * answer when there is one, an answer box for the instructor, and a withdraw
 * button for the student's own unanswered questions.
 */
export function QuestionBubble({ question, isInstructor, onAnswer, onDelete }) {
  const [expanded, setExpanded] = useState(false)
  const [answerText, setAnswerText] = useState("")
  const [saving, setSaving] = useState(false)

  const answered = question.status === "ANSWERED"
  const iconSrc = answered ? questionBubbleIconAnswered : questionBubbleIconUnanswered
  const askerName = [question.asker_first_name, question.asker_last_name]
    .filter(Boolean)
    .join(" ")

  const submitAnswer = async () => {
    if (!answerText.trim() || !onAnswer) return
    setSaving(true)
    try {
      await onAnswer(question.guid, answerText.trim())
      setAnswerText("")
      setExpanded(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box
      width="100%"
      maxWidth="440px"
      borderRadius={expanded ? "16px" : "24px"}
      border="1px solid"
      borderColor={c.border}
      background={c.surface}
      transition="border-radius 0.15s"
    >
      {/* collapsed row */}
      <Flex
        align="center"
        gap="12px"
        padding="10px 14px"
        cursor="pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <Box flex="1" minWidth="0">
          <Text
            fontSize="12px"
            fontWeight="500"
            color={c.text}
            noOfLines={expanded ? undefined : 1}
          >
            {question.question_text}
          </Text>
          {isInstructor && askerName && (
            <Text fontSize="10.5px" color={c.textMuted} mt="1px">
              {askerName}
            </Text>
          )}
        </Box>
        <Tooltip
          label={answered ? "Answered" : "Waiting for an answer"}
          hasArrow
          placement="top"
          fontSize="11px"
        >
          <Image src={iconSrc} boxSize="16px" flexShrink={0} alt={answered ? "Answered" : "Unanswered"} />
        </Tooltip>
      </Flex>

      {/* expanded detail */}
      {expanded && (
        <Box px="14px" pb="12px">
          {answered && question.answer_text && (
            <Box
              background={c.surfaceSubtle}
              borderRadius="10px"
              padding="10px 12px"
              mb="4px"
            >
              <Text fontSize="10.5px" fontWeight="600" color={c.textMuted} textTransform="uppercase" letterSpacing="0.04em" mb="4px">
                Teacher's answer
              </Text>
              <Text fontSize="12px" color={c.textSub} lineHeight="1.6">
                {question.answer_text}
              </Text>
            </Box>
          )}

          {answered && !question.answer_text && (
            <Text fontSize="12px" color={c.textMuted} fontStyle="italic">
              Marked as answered by your teacher.
            </Text>
          )}

          {!answered && isInstructor && (
            <Box>
              <Textarea
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Write your answer…"
                fontSize="12px"
                minHeight="64px"
                resize="none"
                borderRadius="10px"
                background={c.inputBg}
                color={c.text}
                border="1px solid"
                borderColor={c.inputBorder}
                _placeholder={{ color: c.textMuted }}
                _hover={{ borderColor: c.textMuted }}
                _focus={{ borderColor: c.accent, boxShadow: `0 0 0 1px ${c.accent}` }}
              />
              <Flex justify="flex-end" mt="8px">
                <Button
                  size="xs"
                  variant="unstyled"
                  display="inline-flex"
                  alignItems="center"
                  px="14px"
                  height="28px"
                  borderRadius="full"
                  background={c.solidBg}
                  color={c.solidText}
                  fontSize="11.5px"
                  fontWeight="600"
                  isDisabled={!answerText.trim() || saving}
                  opacity={!answerText.trim() || saving ? 0.6 : 1}
                  _hover={{ background: c.solidBg, opacity: 0.86 }}
                  onClick={(e) => {
                    e.stopPropagation()
                    submitAnswer()
                  }}
                >
                  {saving ? "Sending…" : "Send answer"}
                </Button>
              </Flex>
            </Box>
          )}

          {!answered && !isInstructor && (
            <Flex align="center" justify="space-between">
              <Text fontSize="11.5px" color={c.textMuted} fontStyle="italic">
                Your teacher hasn't answered this yet.
              </Text>
              {onDelete && (
                <Tooltip label="Withdraw question" hasArrow placement="top" fontSize="11px">
                  <IconButton
                    aria-label="Withdraw question"
                    icon={<Icon as={HiOutlineTrash} boxSize="13px" />}
                    size="xs"
                    variant="ghost"
                    color={c.textMuted}
                    _hover={{ bg: c.hover }}
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete(question.guid)
                    }}
                  />
                </Tooltip>
              )}
            </Flex>
          )}
        </Box>
      )}
    </Box>
  )
}
